import * as React from 'react';
import { useRefState } from '../helpers/hooks/use-ref-state';
import { Thumb } from './thumb';
const clamp = (min, value, max) => {
    let clamped = value;
    clamped = Math.min(clamped, max);
    clamped = Math.max(clamped, min);
    return clamped;
};
const clampAndStep = (min, value, max, step) => {
    if (!step) {
        return clamp(min, value, max);
    }
    if (Array.isArray(step)) {
        // naive solution, but seems performant enough so far
        const nearest = step
            .filter((s) => s >= min && s <= max)
            .find((current, i, all) => {
            if (i === all.length - 1) {
                return true;
            }
            const next = all[i + 1];
            const cutoff = current + (next - current) / 2;
            return value < cutoff;
        });
        if (typeof nearest !== 'number') {
            // this should never happen, because we add min/max.
            return min;
        }
        return clamp(min, nearest, max);
    }
    // calculate maximum value that is reachable with standard steps
    // e.g. min=0, max=100, step=9 -> maxStepValue = 99.
    const maxStepValue = Math.floor((max - min) / step) * step + min;
    // calculate threshold value where we replace value with max, instead of the nearest step.
    // e.g. max=100, maxStepValue = 99 -> maxStepOrMaxThreshold = 99.5
    const stepOrMaxCutoff = max - (max - maxStepValue) / 2;
    // Calculate value into
    const steppedValue = value > stepOrMaxCutoff
        ? max
        : Math.round((value - min) / step) * step + min;
    return clamp(min, steppedValue, max);
};
/** When one thumb changes, it is by default allowed to push the surrounding
 * thumbs along the rail - This needs to be calculated in both directions of the
 * moving thumb. (Think of it as a ripple effect)  */
const applyPushEffects = (values, fromIndex, min, max, steps) => {
    // push effects for thumbs at lower index
    for (let thumbIndex = fromIndex - 1; thumbIndex >= 0; thumbIndex--) {
        values[thumbIndex] = clampAndStep(min, values[thumbIndex], values[thumbIndex + 1], steps);
    }
    // push effects for thumbs at higher index
    for (let thumbIndex = fromIndex + 1; thumbIndex < values.length; thumbIndex++) {
        values[thumbIndex] = clampAndStep(values[thumbIndex - 1], values[thumbIndex], max, steps);
    }
};
export const Thumbs = ({ minRangeBetweenThumbs, thumbs, min, max, rigidThumbs, onChange, onChangeImmediate, step, getValueText, highlights, hideValueBubbles, ariaDescribedBy, railElementRef, appearance, stateValues, setStateValues, stateValuesRef, defaultValues, }) => {
    const focusHandlers = React.useMemo(() => stateValuesRef.current.map((_, index) => () => {
        setThumbInteractionHistory((old) => [
            ...old.filter((i) => i !== index),
            index,
        ]);
    }), [stateValuesRef]);
    const [slidingState, setSlidingState, slidingStateRef] = useRefState(thumbs.map(() => false));
    const slideChangeHandlers = React.useMemo(() => stateValuesRef.current.map((_, index) => (isSliding) => {
        const newState = [...slidingStateRef.current];
        newState[index] = isSliding;
        setSlidingState(newState);
        // onChange needs to be triggered when sliding stops
        if (!isSliding) {
            onChange === null || onChange === void 0 ? void 0 : onChange(stateValuesRef.current);
        }
    }), [setSlidingState, slidingStateRef, stateValuesRef, onChange]);
    const updateValues = React.useCallback((newValues) => {
        var _a;
        const isSliding = slidingStateRef.current.some(Boolean);
        (_a = (isSliding ? onChangeImmediate : onChange)) === null || _a === void 0 ? void 0 : _a(newValues);
        setStateValues(newValues);
    }, [slidingStateRef, onChangeImmediate, onChange, setStateValues]);
    /** this adds min and max, and dedupes a given step array */
    const wellFormedSteps = React.useMemo(() => Array.isArray(step)
        ? [min, ...step, max]
            .sort((a, b) => a - b)
            .filter((value, index, all) => all.indexOf(value) === index)
        : step, [step, min, max]);
    const valueChangeHandlers = React.useMemo(() => stateValuesRef.current.map((_, index) => (update) => {
        var _a, _b;
        const thumb = thumbs[index];
        const proposedNewValue = clampAndStep((_a = thumb.min) !== null && _a !== void 0 ? _a : min, update(stateValuesRef.current[index]), (_b = thumb.max) !== null && _b !== void 0 ? _b : max, wellFormedSteps);
        if (stateValuesRef.current[index] === proposedNewValue) {
            return;
        }
        const newValues = [...stateValuesRef.current];
        newValues[index] = proposedNewValue;
        if (!rigidThumbs) {
            applyPushEffects(newValues, index, min, max, wellFormedSteps);
        }
        const thumbMin = index > 0 ? newValues[index - 1] + minRangeBetweenThumbs : min;
        const thumbMax = index < newValues.length - 1
            ? newValues[index + 1] - minRangeBetweenThumbs
            : max;
        newValues[index] = clampAndStep(thumbMin, newValues[index], thumbMax, wellFormedSteps);
        return updateValues(newValues);
    }), [
        max,
        min,
        minRangeBetweenThumbs,
        rigidThumbs,
        stateValuesRef,
        thumbs,
        updateValues,
        wellFormedSteps,
    ]);
    /** keyboard arrows need to have "steps" for navigation, even if it is not
     * set - defaulting to 1 percent */
    const keyboardSteps = React.useMemo(() => wellFormedSteps !== null && wellFormedSteps !== void 0 ? wellFormedSteps : (max - min) / 100, [wellFormedSteps, min, max]);
    // ordered list of thumb indices the user last interacted with
    const [thumbInteractionHistory, setThumbInteractionHistory] = React.useState(defaultValues.map((_, i) => i).reverse());
    const lastDefaultValues = React.useRef(defaultValues);
    // reset to defaultValues if they change
    React.useEffect(() => {
        const changed = defaultValues.length !== lastDefaultValues.current.length ||
            defaultValues.some((value, index) => value !== lastDefaultValues.current[index]);
        if (changed) {
            lastDefaultValues.current = defaultValues;
            updateValues(defaultValues);
            setSlidingState(defaultValues.map(() => false));
            setThumbInteractionHistory(defaultValues.map((_, i) => i).reverse());
        }
    }, [defaultValues, max, setSlidingState, updateValues]);
    return (React.createElement(React.Fragment, null, thumbs.map((thumb, index) => {
        var _a, _b, _c;
        const value = stateValues[index];
        const valueText = ((_a = thumb.getValueText) !== null && _a !== void 0 ? _a : getValueText)(value);
        const highlight = highlights.find((highlight) => Math.abs(highlight.value - value) < 0.0001);
        const ariaValueText = [valueText, highlight === null || highlight === void 0 ? void 0 : highlight.label].filter(Boolean).join(' , ') ||
            undefined;
        const isBuried = stateValues.some((otherValue, otherIndex) => otherIndex !== index &&
            otherValue === value &&
            thumbInteractionHistory.indexOf(otherIndex) >
                thumbInteractionHistory.indexOf(index));
        const baseMinValue = (_b = thumb.min) !== null && _b !== void 0 ? _b : min;
        const thumbMin = index > 0
            ? stateValues[index - 1] + minRangeBetweenThumbs
            : baseMinValue;
        const baseMaxValue = (_c = thumb.max) !== null && _c !== void 0 ? _c : max;
        const thumbMax = index < stateValues.length - 1
            ? stateValues[index + 1] - minRangeBetweenThumbs
            : baseMaxValue;
        return (React.createElement(Thumb, { key: `${thumbMin}-${thumbMax}`, value: value, valueText: hideValueBubbles ? undefined : valueText, ariaValueText: ariaValueText, isSliding: slidingState[index], "aria-label": thumb.ariaLabel, "aria-labelledby": thumb.ariaLabelledby, "aria-describedby": ariaDescribedBy, railElementRef: railElementRef, thumbMin: thumbMin, thumbMax: thumbMax, appearance: appearance, zIndex: thumbInteractionHistory.indexOf(index) + 3, isBuried: isBuried, sliderMin: min, sliderMax: max, onValueChange: valueChangeHandlers[index], onSlideChange: slideChangeHandlers[index], onFocus: focusHandlers[index], keyboardSteps: keyboardSteps }));
    })));
};
